"use client";

import { cn, formatPrice } from "@/lib/utils";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Link from "next/link";
import { useCart } from "@/components/cart/cart-provider";
import { useSession } from "next-auth/react";
import { useCartStore } from "@/store/cart-store";
// Removed direct server action import
import { CartItem, UserSession, Cart, CartItemWithGroup } from "@/types";
import { pusherClient } from "@/lib/pusher";
import { useSwipeable } from "react-swipeable";

import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  ShoppingCart,
  Trash2,
  Download,
  Search,
  CloudIcon,
  CloudOffIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import EmptyCart from "@/components/cart/empty-cart";
import { CartItems } from "@/components/cart/cart-items";
import { toast } from "@/hooks/use-toast";
import BrowserTabs from "@/components/ui/browser-tabs";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@radix-ui/react-scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";

interface CartButtonProps {
  className?: string;
  isMobile?: boolean;
  disabled?: boolean;
}

// Utility function to remove circular references from cart items
function prepareCartForStorage(cart: Cart): Cart {
  if (!cart) return cart;

  try {
    // Create a deep copy of the cart without circular references
    const preparedCart = {
      ...cart,
      cart_items: cart.cart_items
        ? cart.cart_items.map((item) => {
            // Create a new item without _group and _quoteNumber
            const itemWithGroup = item as CartItemWithGroup;
            const cleanItem: CartItem = {
              item_id: itemWithGroup.item_id,
              product_id: itemWithGroup.product_id,
              tier_id: itemWithGroup.tier_id,
              price: itemWithGroup.price,
              title: itemWithGroup.title,
              part_number: itemWithGroup.part_number,
              image: itemWithGroup.image,
              weight: itemWithGroup.weight,
              width: itemWithGroup.width,
              height: itemWithGroup.height,
              length: itemWithGroup.length,
              moq: itemWithGroup.moq,
              shipping_weight: itemWithGroup.shipping_weight,
              shipping_width: itemWithGroup.shipping_width,
              shipping_height: itemWithGroup.shipping_height,
              shipping_length: itemWithGroup.shipping_length,
              decal_number: itemWithGroup.decal_number,
              attachments: itemWithGroup.attachments,
              quantity: itemWithGroup.quantity,
            };

            // Handle attachments safely by also removing circular references
            if (cleanItem.attachments && Array.isArray(cleanItem.attachments)) {
              cleanItem.attachments = cleanItem.attachments.map((att) => {
                // Handle potential circular references in attachments too
                const attWithGroup = att as CartItemWithGroup;
                return {
                  item_id: attWithGroup.item_id,
                  product_id: attWithGroup.product_id,
                  part_number: attWithGroup.part_number,
                  decal_number: attWithGroup.decal_number,
                  price: attWithGroup.price || 0,
                  title: attWithGroup.title || "",
                  image: attWithGroup.image,
                  tier_id: attWithGroup.tier_id,
                  quantity: attWithGroup.quantity || 1,
                  shipping_weight: attWithGroup.shipping_weight || 0,
                  shipping_width: attWithGroup.shipping_width,
                  shipping_height: attWithGroup.shipping_height,
                  shipping_length: attWithGroup.shipping_length,
                  weight: attWithGroup.weight || 0,
                  width: attWithGroup.width,
                  height: attWithGroup.height,
                  length: attWithGroup.length,
                  moq: attWithGroup.moq || 1,
                };
              });
            }

            return cleanItem;
          })
        : [],
    };

    // Verify it's serializable
    try {
      JSON.stringify(preparedCart);
      return preparedCart;
    } catch (error) {
      console.error(
        "[CartButton] Safe cart still has serialization issues:",
        error
      );
      // Fall back to a minimal safe approach if there are still issues
      return {
        ...cart,
        cart_items: [], // Empty cart items to be safe
      };
    }
  } catch (e) {
    console.error("[CartButton] Error in prepareCartForStorage:", e);
    // Return a minimal safe version
    return {
      ...cart,
      cart_items: [],
    };
  }
}

// Function to get the cart items count from a cart items array
const getTotalItemCount = (cartItems: CartItem[] = []): number => {
  if (!Array.isArray(cartItems)) return 0;

  // Count total items, rather than unique products
  // We need to count actual items for local carts where quantity is tracked by duplicate items
  return cartItems.length;
};

// Function to get cart data directly from localStorage for immediate rendering
const getLocalCartData = (): Cart | null => {
  if (typeof window === "undefined") return null;

  try {
    const cartData = localStorage.getItem("active-cart-data");
    if (!cartData) return null;

    const parsedCart = JSON.parse(cartData) as Cart;
    return parsedCart;
  } catch (error) {
    console.error("Failed to read cart data from storage:", error);
    return null;
  }
};

// Update local storage with cart data
const updateLocalCartData = (cart: Cart): void => {
  if (typeof window === "undefined") return;

  try {
    // Clean the cart before storing in localStorage
    const cleanCart = prepareCartForStorage(cart);

    localStorage.setItem("active-cart-data", JSON.stringify(cleanCart));
  } catch (error) {
    console.error("Failed to update localStorage:", error);
  }
};

// Add this function after the getLocalCartData and updateLocalCartData functions
// Function to ensure a cart exists in localStorage
const ensureLocalCart = (session?: UserSession): Cart | null => {
  // First try to get existing cart data
  const existingCart = getLocalCartData();

  // If we're on the success page and have an existing cart, return it
  if (
    typeof window !== "undefined" &&
    window.location.pathname.includes("/checkout/success") &&
    existingCart
  ) {
    return existingCart;
  }

  if (existingCart) {
    return existingCart;
  }

  // Check if user-carts exists in localStorage
  if (typeof window !== "undefined") {
    try {
      const userCartsData = localStorage.getItem("user-carts");
      if (userCartsData) {
        // If user-carts exists but active-cart-data doesn't, recreate it
        const parsedUserCarts = JSON.parse(userCartsData);
        if (
          parsedUserCarts?.state?.carts?.length > 0 &&
          parsedUserCarts?.state?.activeQuoteNumber
        ) {
          // Find the active cart in the user-carts state
          const activeCart = parsedUserCarts.state.carts.find(
            (cart: Cart) =>
              cart.quote_number === parsedUserCarts.state.activeQuoteNumber
          );

          if (activeCart) {
            // Update active-cart-data with the found cart
            localStorage.setItem(
              "active-cart-data",
              JSON.stringify(activeCart)
            );
            return activeCart;
          }
        }
      }
    } catch (error) {
      console.error("[CartButton] Error checking user-carts:", error);
    }
  }

  // For authenticated users, don't create local carts - let the cart provider handle it
  if (session) {
    return null;
  }

  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 9);
  const quoteNumber = `QT-GUEST-${timestamp}-${randomId}`;

  const newCart: Cart = {
    id: `local-${timestamp}`,
    quote_number: quoteNumber,
    name: "Cart",
    user_data: {
      role: "user" as const,
      id: null,
      email: null,
      firstName: null,
      lastName: null,
      emailVerified: null,
      jobTitle: null,
      phone: null,
      image: null,
      companyId: null,
      companyName: null,
      companyImage: null,
      paymentTerms: "cash_on_delivery" as const,
      stripeCustomerId: null,
      tierId: null,
      addressId: null,
      permissions: {},
    },
    cart_items: [],
    printed: false,
    discarded: false,
    purchased: false,
    notes: "",
    created: new Date(),
    updated: new Date(),
    isServerCart: false,
  };

  // Save new cart to localStorage
  if (typeof window !== "undefined") {
    try {
      // Create and save active-cart-data
      localStorage.setItem(
        "active-cart-data",
        JSON.stringify(prepareCartForStorage(newCart))
      );

      // Create and save user-carts
      const initialState = {
        state: {
          carts: [newCart],
          activeQuoteNumber: quoteNumber,
          initialized: true,
        },
        version: 0,
      };
      localStorage.setItem("user-carts", JSON.stringify(initialState));

      // Also directly update the Zustand store
      const store = useCartStore.getState();
      store.addCart(newCart);
      store.setActiveCart(quoteNumber);
    } catch (error) {
      console.error("[CartButton] Error creating default cart:", error);
    }
  }

  return newCart;
};

// Utility function to clean cart items of circular references before server action calls
const cleanCartItemsForServerAction = (items: CartItem[]): CartItem[] => {
  return items.map((item) => {
    // Create a clean copy without circular reference properties
    const cleanItem = { ...item };

    // Remove problematic properties
    if ("_group" in cleanItem) {
      delete (cleanItem as CartItemWithGroup)._group;
    }
    if ("_quoteNumber" in cleanItem) {
      delete (cleanItem as CartItemWithGroup)._quoteNumber;
    }

    // Clean attachments too
    if (cleanItem.attachments && Array.isArray(cleanItem.attachments)) {
      cleanItem.attachments = cleanItem.attachments.map((att) => {
        const cleanAtt = { ...att };
        if ("_group" in cleanAtt) {
          delete (cleanAtt as CartItemWithGroup)._group;
        }
        if ("_quoteNumber" in cleanAtt) {
          delete (cleanAtt as CartItemWithGroup)._quoteNumber;
        }
        return cleanAtt;
      });
    }

    return cleanItem;
  });
};

// Extend Window interface for our custom property
declare global {
  interface Window {
    __cartMigrationLastEventIdRef?: { current: string | null };
    __cartDeletionToastSet?: Set<string>;
  }
}

// Shared event deduplication ref
let lastEventIdRef: { current: string | null };
if (typeof window !== "undefined") {
  if (!window.__cartMigrationLastEventIdRef) {
    window.__cartMigrationLastEventIdRef = { current: null };
  }
  lastEventIdRef = window.__cartMigrationLastEventIdRef;

  if (!window.__cartDeletionToastSet) {
    window.__cartDeletionToastSet = new Set<string>();
  }
} else {
  lastEventIdRef = { current: null };
}

// Helper to determine if a cart is both printed and older than 30 days (expired)
const isExpiredPrinted = (cart: Cart): boolean => {
  if (!cart?.printed) return false;
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const createdDate =
    cart.created instanceof Date ? cart.created : new Date(cart.created);
  return createdDate < thirtyDaysAgo;
};

const CartButton: React.FC<CartButtonProps> = ({
  className,
  isMobile,
  disabled,
}) => {
  const isMobileDevice = useIsMobile();
  const {
    carts,
    activeQuoteNumber,
    isLoading,
    isDataSourceReady,
    clearCart,
    setActiveCart,
    closeCartTab,
    convertGuestCartToServer,
    renameCart,
    getCart,
    addCart,
    updateCartsOrder,
    generateQuotePDF,
  } = useCart();
  const { data: session, status } = useSession();

  // Filter out carts that are purchased OR printed & older than 30 days
  const availableCarts = useMemo(() => {
    return carts.filter((cart) => !cart.purchased && !isExpiredPrinted(cart));
  }, [carts]);

  const [isMounted, setIsMounted] = useState(false);
  const [isLoadingQuote, setIsLoadingQuote] = useState(false);
  const [quoteSearchOpen, setQuoteSearchOpen] = useState(false);
  const [searchQuoteNumber, setSearchQuoteNumber] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [tabsKey, setTabsKey] = useState(0);
  const [persistentTabId, setPersistentTabId] = useState<string | null>(null);
  const [renamingCartId, setRenamingCartId] = useState<string | null>(null);
  const [clearCartAlertOpen, setClearCartAlertOpen] = useState(false);
  const [isClearingCart, setIsClearingCart] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Add direct local storage access for initial render
  const [localCartData, setLocalCartData] = useState<Cart | null>(null);
  const [localCartItemCount, setLocalCartItemCount] = useState(0);
  const [isAddingNewCart, setIsAddingNewCart] = useState(false);

  const lastSyncTimeRef = useRef<number>(Date.now());

  // Determine if we should show loading state
  const shouldShowLoading = useMemo(() => {
    // Show loading if:
    // 1. Authentication is loading
    // 2. Cart provider is loading
    // 3. We have an authenticated user but no carts and data source isn't ready
    const authLoading = status === "loading";
    const cartProviderLoading = isLoading;
    const authenticatedWithoutCarts =
      status === "authenticated" &&
      session?.user &&
      availableCarts.length === 0 &&
      !isDataSourceReady;

    return authLoading || cartProviderLoading || authenticatedWithoutCarts;
  }, [status, session, isLoading, availableCarts.length, isDataSourceReady]);

  // Function to save open server carts to local storage
  const saveOpenServerCarts = useCallback((quoteNumbers: string[]) => {
    if (typeof window === "undefined") return;

    try {
      // Check for and remove any duplicates
      const uniqueQuoteNumbers = Array.from(new Set(quoteNumbers));

      // Using the cart store to update open carts
      const store = useCartStore.getState();

      // Update the cart store directly
      store.updateCartsOrder(uniqueQuoteNumbers);
    } catch (error) {
      console.error(
        "[CartButton] Failed to update server carts in store:",
        error
      );
    }
  }, []);

  // Move the activeCart memo before it's used in the effect
  // Use the active cart from the provider, falling back to local storage if needed
  const activeCart = useMemo(() => {
    if (activeQuoteNumber) {
      // Try to find the active cart in the provider (ensure it's not expired)
      const providerCart = availableCarts.find(
        (cart) => cart.quote_number === activeQuoteNumber
      );
      if (providerCart && !isExpiredPrinted(providerCart)) {
        return providerCart;
      }
    }

    if (
      localCartData &&
      !isExpiredPrinted(localCartData) &&
      (!activeQuoteNumber || localCartData.quote_number === activeQuoteNumber)
    ) {
      return localCartData;
    }

    return null;
  }, [activeQuoteNumber, availableCarts, localCartData]);

  // Always ensure cart_items is an array
  const items = useMemo(
    () => (Array.isArray(activeCart?.cart_items) ? activeCart.cart_items : []),
    [activeCart?.cart_items]
  );

  const refreshTabs = useCallback(() => {
    // Use a timestamp-based key with added entropy to ensure uniqueness
    const newKey = Date.now();

    // Forcefully update the tabs key to trigger a complete re-render
    setTabsKey(newKey);

    // Force immediate state updates and DOM refresh
    setTimeout(() => {
      // Grab the latest cart data directly from localStorage
      const cartDataFromStorage = getLocalCartData();
      if (cartDataFromStorage) {
        setLocalCartData(cartDataFromStorage);
        setLocalCartItemCount(cartDataFromStorage.cart_items?.length || 0);
      }

      // Force a second UI update with a delay to catch any async updates
      setTimeout(() => {
        setTabsKey((prev) => prev + 1);
      }, 50);
    }, 0);
  }, []);

  const handleCloseCartTab = useCallback(
    async (quoteNumber: string) => {
      // Get cart being closed for reference
      const cartBeingClosed = availableCarts.find(
        (c) => c.quote_number === quoteNumber
      );

      // Clean up localStorage immediately for the active cart
      if (activeQuoteNumber === quoteNumber) {
        try {
          localStorage.removeItem("active-cart-data");
        } catch (e) {
          console.error("[CartButton] Error cleaning localStorage:", e);
        }
      }

      // Perform server operation in background without blocking UI
      closeCartTab(quoteNumber)
        .then(() => {
          // Display appropriate toast message after successful server operation
          if (session && cartBeingClosed?.isServerCart) {
            const hasItems =
              cartBeingClosed.cart_items &&
              cartBeingClosed.cart_items.length > 0;

            if (!hasItems) {
              if (
                typeof window !== "undefined" &&
                !window.__cartDeletionToastSet?.has(quoteNumber)
              ) {
                toast({
                  title: "Cart Deleted",
                  description: "Empty cart has been permanently deleted.",
                });
                window.__cartDeletionToastSet?.add(quoteNumber);
                // Clean up after short delay so future deletions can show toast
                setTimeout(() => {
                  window.__cartDeletionToastSet?.delete(quoteNumber);
                }, 2000);
              }
            } else {
              if (
                typeof window !== "undefined" &&
                !window.__cartDeletionToastSet?.has(quoteNumber)
              ) {
                toast({
                  title: "Cart Hidden",
                  description:
                    "The cart is only hidden, not deleted. You can find it again using the search button.",
                });
                window.__cartDeletionToastSet?.add(quoteNumber);
                setTimeout(() => {
                  window.__cartDeletionToastSet?.delete(quoteNumber);
                }, 2000);
              }
            }
          }
        })
        .catch((err) => {
          console.error("[CartButton] Error in background cart deletion:", err);

          // Show error toast but don't restore the tab since it's already gone from UI
          toast({
            title: "Warning",
            description:
              "Cart removed from view but server operation failed. The cart may still exist on the server.",
            variant: "destructive",
          });
        });

      // Force a refresh to ensure UI consistency
      setTimeout(() => {
        refreshTabs();
      }, 100);
    },
    [availableCarts, activeQuoteNumber, closeCartTab, refreshTabs, session]
  );

  useEffect(() => {
    setIsMounted(true);

    // On first mount, ensure a cart exists (but not on success page)
    // For authenticated users, let the cart provider handle cart initialization
    const cartData = ensureLocalCart(session?.user);
    if (cartData) {
      setLocalCartData(cartData);
      setLocalCartItemCount(cartData.cart_items?.length || 0);

      // If we don't have a persistent tab ID yet, set it from the cart
      if (!persistentTabId) {
        setPersistentTabId(cartData.quote_number);
      }

      // Ensure the active cart is set in store
      if (cartData.quote_number) {
        const store = useCartStore.getState();
        if (!store.activeQuoteNumber) {
          store.setActiveCart(cartData.quote_number);
        }
      }
    }

    // Function to sync active cart from server
    const syncActiveCartFromServer = async () => {
      if (!activeQuoteNumber) return;

      const activeCartData = carts.find(
        (cart) => cart.quote_number === activeQuoteNumber
      );

      if (!activeCartData?.isServerCart) return;

      try {
        const result = await getCart(activeQuoteNumber);

        if (result.success && result.cart) {
          // Update local storage with the fresh server data
          const cleanCart = prepareCartForStorage(result.cart);
          localStorage.setItem("active-cart-data", JSON.stringify(cleanCart));

          // Update local state
          setLocalCartData(result.cart);
          setLocalCartItemCount(result.cart.cart_items?.length || 0);
        }
      } catch (error) {
        console.error(
          `[CartButton] Error syncing active cart from server:`,
          error
        );
      }
    };

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const timeSinceLastSync = Date.now() - lastSyncTimeRef.current;

        // Only sync if it's been more than 5 seconds since last sync
        if (timeSinceLastSync > 5000) {
          syncActiveCartFromServer();
          lastSyncTimeRef.current = Date.now();
        }
      }
    };

    // Handle online event
    const handleOnline = () => {
      syncActiveCartFromServer();
      lastSyncTimeRef.current = Date.now();
    };

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);

    // Set up Pusher subscription for real-time cart updates
    let cartChannel: ReturnType<typeof pusherClient.subscribe> | null = null;

    if (cartData?.quote_number && cartData?.isServerCart) {
      cartChannel = pusherClient.subscribe(`cart-${cartData.quote_number}`);

      // Listen for cart item updates
      cartChannel.bind(
        "cart-updated",
        (data: {
          type: string;
          itemsAdded?: number;
          itemsRemoved?: number;
          itemCount?: number;
          pricesChanged?: boolean;
        }) => {
          if (data.type === "bulk-operation") {
            // Force refresh of cart data from server
            if (
              activeQuoteNumber === cartData.quote_number &&
              cartData.isServerCart
            ) {
              // For server carts, fetch from server to sync across devices
              setTimeout(async () => {
                try {
                  const result = await getCart(cartData.quote_number);

                  if (result.success && result.cart) {
                    // Update local storage with the fresh server data
                    const cleanCart = prepareCartForStorage(result.cart);
                    localStorage.setItem(
                      "active-cart-data",
                      JSON.stringify(cleanCart)
                    );

                    // Update local state
                    setLocalCartData(result.cart);
                    setLocalCartItemCount(result.cart.cart_items?.length || 0);
                  }
                } catch (error) {
                  console.error(
                    `[CartButton] Error fetching cart from server:`,
                    error
                  );
                  // Fall back to localStorage update
                  const updatedCartData = getLocalCartData();
                  if (updatedCartData) {
                    setLocalCartData(updatedCartData);
                    setLocalCartItemCount(
                      updatedCartData.cart_items?.length || 0
                    );
                  }
                }
                refreshTabs();
              }, 100);
            }
          } else if (data.type === "prices-updated") {
            // Refresh cart data to show updated prices
            setTimeout(() => {
              const updatedCartData = getLocalCartData();
              if (updatedCartData) {
                setLocalCartData(updatedCartData);
              }
              refreshTabs();
            }, 100);
          }
        }
      );

      // Listen for cart deletion
      cartChannel.bind("cart-deleted", (data: { quoteNumber: string }) => {
        if (data.quoteNumber === cartData.quote_number) {
          // This cart was deleted, close it
          handleCloseCartTab(data.quoteNumber);
        }
      });
    }

    // Listen for cart migration events
    const handleCartMigrationComplete = (event: CustomEvent) => {
      const { convertedCarts, updatedCarts, eventId } = event.detail;
      // Deduplicate: Only show toast if this eventId hasn't been handled yet
      if (lastEventIdRef.current === eventId) return;
      lastEventIdRef.current = eventId;

      // Clear any guest cart data from localStorage since it's been migrated
      try {
        const cartData = getLocalCartData();
        if (cartData && cartData.quote_number?.startsWith("QT-GUEST-")) {
          localStorage.removeItem("active-cart-data");
        }
      } catch (error) {
        console.error(
          "[CartButton] Error clearing guest cart data after migration:",
          error
        );
      }

      let message = "";

      if (convertedCarts > 0 && updatedCarts > convertedCarts) {
        message = `Welcome back! We've saved your ${convertedCarts} cart${convertedCarts === 1 ? "" : "s"} and updated ${updatedCarts} existing cart${updatedCarts === 1 ? "" : "s"} to your account.`;
      } else if (convertedCarts > 0) {
        message = `Welcome back! We've saved your ${convertedCarts} cart${convertedCarts === 1 ? "" : "s"} to your account.`;
      } else if (updatedCarts > 0) {
        message = `Welcome back! We've updated ${updatedCarts} cart${updatedCarts === 1 ? "" : "s"} with your account information.`;
      }

      if (message) {
        toast({
          title: "Carts Synchronized",
          description: message,
        });
      }
    };

    const handleCartMigrationError = (event: CustomEvent) => {
      console.error("[CartButton] Cart migration error:", event.detail.error);

      toast({
        title: "Cart Sync Warning",
        description:
          "Some carts may not have been properly synchronized. Your items are still safe in your cart.",
        variant: "destructive",
      });
    };

    // Listen for active cart changes from success page
    const handleCartActiveChanged = (event: CustomEvent) => {
      const { quoteNumber } = event.detail;

      // Update persistent tab ID and force refresh
      setPersistentTabId(quoteNumber);
      refreshTabs();

      // Also update local cart data if needed
      const cartData = getLocalCartData();
      if (cartData && cartData.quote_number === quoteNumber) {
        setLocalCartData(cartData);
        setLocalCartItemCount(cartData.cart_items?.length || 0);
      }
    };

    // Add event listeners
    window.addEventListener(
      "cart-migration-complete",
      handleCartMigrationComplete as EventListener
    );
    window.addEventListener(
      "cart-migration-error",
      handleCartMigrationError as EventListener
    );
    window.addEventListener(
      "cart-active-changed",
      handleCartActiveChanged as EventListener
    );

    // Cleanup event listeners and Pusher subscriptions
    return () => {
      // Remove visibility and online listeners
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);

      // Cleanup Pusher subscription
      if (cartChannel && cartData?.quote_number) {
        cartChannel.unbind_all();
        pusherClient.unsubscribe(`cart-${cartData.quote_number}`);
      }

      window.removeEventListener(
        "cart-migration-complete",
        handleCartMigrationComplete as EventListener
      );
      window.removeEventListener(
        "cart-migration-error",
        handleCartMigrationError as EventListener
      );
      window.removeEventListener(
        "cart-active-changed",
        handleCartActiveChanged as EventListener
      );
    };
  }, [
    activeQuoteNumber,
    carts,
    getCart,
    handleCloseCartTab,
    persistentTabId,
    refreshTabs,
    session?.user,
  ]); // Added dependencies

  // Update local cart item count whenever cart changes
  useEffect(() => {
    if (isMounted && activeCart) {
      const currentCount = getTotalItemCount(activeCart.cart_items);
      setLocalCartItemCount(currentCount);

      // Also update local storage with the latest cart data
      updateLocalCartData(activeCart);

      // Ensure localCartData is also updated when activeCart changes
      setLocalCartData(activeCart);
    }
  }, [isMounted, activeCart]);

  // Separate effect for persistentTabId initialization
  useEffect(() => {
    if (!persistentTabId && isMounted) {
      const cartData = getLocalCartData();
      if (cartData) {
        setPersistentTabId(cartData.quote_number);
      }
    }
  }, [persistentTabId, isMounted]);

  useEffect(() => {
    if (
      isMounted &&
      activeQuoteNumber &&
      persistentTabId !== activeQuoteNumber
    ) {
      const cartExists = availableCarts.some(
        (cart) => cart.quote_number === activeQuoteNumber
      );

      if (cartExists) {
        setPersistentTabId(activeQuoteNumber);
      }
    }
  }, [isMounted, activeQuoteNumber, persistentTabId, availableCarts]);

  // Add an effect to update the UI when the active cart changes
  useEffect(() => {
    if (isMounted && activeQuoteNumber) {
      // If the active quote number from the cart provider changes,
      // update the persistent tab ID for the browser tabs
      if (persistentTabId !== activeQuoteNumber) {
        setPersistentTabId(activeQuoteNumber);

        // Also force a refresh of the tabs
        refreshTabs();
      }
    }
  }, [isMounted, activeQuoteNumber, persistentTabId, refreshTabs]);

  // Add effect to handle when cart becomes empty
  useEffect(() => {
    if (isMounted && activeCart) {
      const itemCount = activeCart.cart_items?.length || 0;

      // Update the local count if it changed
      if (itemCount !== localCartItemCount) {
        setLocalCartItemCount(itemCount);
      }
    }
  }, [
    isMounted,
    activeCart, // Include activeCart to satisfy React Hook dependency rules
    localCartItemCount,
  ]);

  // Wrap handleCloseCartTab in useCallback to prevent it from changing on every render
  const handleCloseCartTabCallback = useCallback(handleCloseCartTab, [
    availableCarts,
    activeQuoteNumber,
    closeCartTab,
    refreshTabs,
    session,
    handleCloseCartTab,
  ]);

  // Add effect to handle Pusher subscriptions for active cart changes
  useEffect(() => {
    if (!isMounted || !activeQuoteNumber) return;

    const activeCartData = availableCarts.find(
      (cart) => cart.quote_number === activeQuoteNumber
    );

    // Only subscribe to server carts
    if (!activeCartData?.isServerCart) return;

    const cartChannel = pusherClient.subscribe(`cart-${activeQuoteNumber}`);

    // Listen for real-time cart updates
    cartChannel.bind(
      "cart-updated",
      (data: {
        type: string;
        itemsAdded?: number;
        itemsRemoved?: number;
        totalItems?: number;
        itemCount?: number;
        pricesChanged?: boolean;
        quoteNumber?: string;
        itemDetails?: {
          title?: string;
          partNumber?: string;
          price?: number;
        };
      }) => {
        // Only process updates for the current active cart
        if (data.quoteNumber && data.quoteNumber !== activeQuoteNumber) {
          return;
        }

        if (data.type === "item-added") {
          // Update local state immediately for better UX
          if (data.totalItems !== undefined) {
            setLocalCartItemCount(data.totalItems);
          }

          // For server carts, fetch the latest data from server to sync across devices
          if (activeCartData?.isServerCart) {
            setTimeout(async () => {
              try {
                const result = await getCart(activeQuoteNumber);

                if (result.success && result.cart) {
                  // Update local storage with the fresh server data
                  const cleanCart = prepareCartForStorage(result.cart);
                  localStorage.setItem(
                    "active-cart-data",
                    JSON.stringify(cleanCart)
                  );

                  // Update local state
                  setLocalCartData(result.cart);
                  setLocalCartItemCount(result.cart.cart_items?.length || 0);
                }
              } catch (error) {
                console.error(
                  `[CartButton] Error fetching cart from server:`,
                  error
                );
                // Fall back to localStorage update
                const updatedCartData = getLocalCartData();
                if (updatedCartData) {
                  setLocalCartData(updatedCartData);
                  setLocalCartItemCount(
                    updatedCartData.cart_items?.length || 0
                  );
                }
              }

              refreshTabs();
            }, 100);
          } else {
            // For local carts, just refresh from localStorage
            setTimeout(() => {
              const updatedCartData = getLocalCartData();
              if (updatedCartData) {
                setLocalCartData(updatedCartData);
                setLocalCartItemCount(updatedCartData.cart_items?.length || 0);
              }
              refreshTabs();
            }, 100);
          }
        } else if (data.type === "item-removed") {
          // Update local state immediately for better UX
          if (data.totalItems !== undefined) {
            setLocalCartItemCount(data.totalItems);
          }

          // For server carts, fetch the latest data from server to sync across devices
          if (activeCartData?.isServerCart) {
            setTimeout(async () => {
              try {
                const result = await getCart(activeQuoteNumber);

                if (result.success && result.cart) {
                  // Update local storage with the fresh server data
                  const cleanCart = prepareCartForStorage(result.cart);
                  localStorage.setItem(
                    "active-cart-data",
                    JSON.stringify(cleanCart)
                  );

                  // Update local state
                  setLocalCartData(result.cart);
                  setLocalCartItemCount(result.cart.cart_items?.length || 0);
                }
              } catch (error) {
                console.error(
                  `[CartButton] Error fetching cart from server:`,
                  error
                );
                // Fall back to localStorage update
                const updatedCartData = getLocalCartData();
                if (updatedCartData) {
                  setLocalCartData(updatedCartData);
                  setLocalCartItemCount(
                    updatedCartData.cart_items?.length || 0
                  );
                }
              }

              refreshTabs();
            }, 100);
          } else {
            // For local carts, just refresh from localStorage
            setTimeout(() => {
              const updatedCartData = getLocalCartData();
              if (updatedCartData) {
                setLocalCartData(updatedCartData);
                setLocalCartItemCount(updatedCartData.cart_items?.length || 0);
              }
              refreshTabs();
            }, 100);
          }
        } else if (data.type === "bulk-operation") {
          // Update local state immediately for better UX
          if (data.totalItems !== undefined) {
            setLocalCartItemCount(data.totalItems);
          }

          // For server carts, fetch the latest data from server to sync across devices
          if (activeCartData?.isServerCart) {
            setTimeout(async () => {
              try {
                const result = await getCart(activeQuoteNumber);

                if (result.success && result.cart) {
                  // Update local storage with the fresh server data
                  const cleanCart = prepareCartForStorage(result.cart);
                  localStorage.setItem(
                    "active-cart-data",
                    JSON.stringify(cleanCart)
                  );

                  // Update local state
                  setLocalCartData(result.cart);
                  setLocalCartItemCount(result.cart.cart_items?.length || 0);
                }
              } catch (error) {
                console.error(
                  `[CartButton] Error fetching cart from server:`,
                  error
                );
                // Fall back to localStorage update
                const updatedCartData = getLocalCartData();
                if (updatedCartData) {
                  setLocalCartData(updatedCartData);
                  setLocalCartItemCount(
                    updatedCartData.cart_items?.length || 0
                  );
                }
              }

              refreshTabs();
            }, 100);
          } else {
            // For local carts, just refresh from localStorage
            setTimeout(() => {
              const updatedCartData = getLocalCartData();
              if (updatedCartData) {
                setLocalCartData(updatedCartData);
                setLocalCartItemCount(updatedCartData.cart_items?.length || 0);
              }
              refreshTabs();
            }, 100);
          }
        } else if (data.type === "prices-updated") {
          toast({
            title: "Prices Updated",
            description: "Cart prices have been updated",
          });

          // Refresh cart data to show updated prices
          setTimeout(() => {
            const updatedCartData = getLocalCartData();
            if (updatedCartData) {
              setLocalCartData(updatedCartData);
            }
            refreshTabs();
          }, 100);
        }
      }
    );

    // Listen for cart deletion
    cartChannel.bind("cart-deleted", (data: { quoteNumber: string }) => {
      if (data.quoteNumber === activeQuoteNumber) {
        if (
          typeof window !== "undefined" &&
          !window.__cartDeletionToastSet?.has(data.quoteNumber)
        ) {
          toast({
            title: "Cart Deleted",
            description: "Empty cart has been permanently deleted.",
          });
          window.__cartDeletionToastSet?.add(data.quoteNumber);
          setTimeout(() => {
            window.__cartDeletionToastSet?.delete(data.quoteNumber);
          }, 2000);
        }
        handleCloseCartTabCallback(data.quoteNumber);
      }
    });

    // Cleanup subscription when effect re-runs or component unmounts
    return () => {
      cartChannel.unbind_all();
      pusherClient.unsubscribe(`cart-${activeQuoteNumber}`);
    };
  }, [
    isMounted,
    activeQuoteNumber,
    availableCarts,
    refreshTabs,
    getCart,
    handleCloseCartTabCallback,
  ]);

  // Update the getTotalPrice function to handle type issues
  const getTotalPrice = (): number => {
    if (!items || !Array.isArray(items)) return 0;

    // Group items by product to account for proper quantity calculation
    const itemMap = new Map<string, { price: number; quantity: number }>();

    // Process each item
    items.forEach((item) => {
      // Create a unique identifier for each distinct product
      const productKey = `${item.part_number}|${item.decal_number || ""}`;

      // Get or initialize the entry
      const entry = itemMap.get(productKey) || {
        price: item.price,
        quantity: 0,
      };

      // Consider both explicit quantity property and individual items
      // Some cart implementations use item.quantity, others use duplicate items
      const itemQuantity = item.quantity || 1;
      entry.quantity += itemQuantity;

      // Update the map
      itemMap.set(productKey, entry);
    });

    // Calculate total by multiplying each item's price by its quantity
    let total = 0;
    itemMap.forEach(({ price, quantity }) => {
      total += price * quantity;
    });

    return total;
  };

  // Add back the clear cart functions
  const handleClearCartClick = useCallback(() => {
    setClearCartAlertOpen(true);
  }, []);

  const handleClearCart = useCallback(async () => {
    if (!activeQuoteNumber) return;

    try {
      setIsClearingCart(true);

      await clearCart(activeQuoteNumber);

      // Check if this is a local cart and update needsMigration flag
      const cart = availableCarts.find(
        (c) => c.quote_number === activeQuoteNumber
      );

      if (cart && !cart.isServerCart) {
        // Update the store to set needsMigration to false
        const store = useCartStore.getState();
        store.updateCart(activeQuoteNumber, {
          needsMigration: false,
        });

        // Also update localStorage
        try {
          const cartData = getLocalCartData();
          if (cartData && cartData.quote_number === activeQuoteNumber) {
            const updatedCart = {
              ...cartData,
              cart_items: [],
              needsMigration: false,
            };
            localStorage.setItem(
              "active-cart-data",
              JSON.stringify(prepareCartForStorage(updatedCart))
            );

            // Update localCartData state to reflect the empty cart
            setLocalCartData(updatedCart);
            setLocalCartItemCount(0);
          }
        } catch (storageError) {
          console.error(
            "[CartButton] Failed to update localStorage:",
            storageError
          );
        }
      } else if (cart) {
        // For server carts, also update local state
        const updatedCart = {
          ...cart,
          cart_items: [],
        };
        setLocalCartData(updatedCart);
        setLocalCartItemCount(0);
      }

      toast({
        title: "Success",
        description: "Cart emptied successfully",
      });
    } catch (err) {
      console.error(
        `[CartButton] Error clearing cart ${activeQuoteNumber}:`,
        err
      );
      toast({
        title: "Error",
        description: "Failed to clear cart: " + err,
        variant: "destructive",
      });
    } finally {
      setIsClearingCart(false);

      // Force a refresh of the tabs to update the UI
      setTimeout(() => {
        refreshTabs();
      }, 100);
    }
  }, [activeQuoteNumber, clearCart, refreshTabs, availableCarts]);

  const handleAddNewCart = async () => {
    try {
      setIsAddingNewCart(true);

      // Get the base cart name
      const baseName = "Cart";

      // Find a unique name that doesn't exist in current carts
      let newName = baseName;
      let counter = 1;

      // Check if there are any carts with the base name
      const baseNameExists = availableCarts.some(
        (cart) => cart.name === baseName
      );

      if (baseNameExists) {
        // Find the next available name by incrementing counter
        while (
          availableCarts.some(
            (cart) => cart.name === `${baseName} (${counter})`
          )
        ) {
          counter++;
        }
        newName = `${baseName} (${counter})`;
      }

      // Create a new cart with the unique name
      const newQuoteNumber = await addCart(newName);

      // Create default user data for the cart
      const defaultUserData: UserSession = {
        id: session?.user?.id || null,
        role: session?.user?.role || "user",
        jobTitle: session?.user?.jobTitle || null,
        email: session?.user?.email || null,
        firstName: session?.user?.firstName || null,
        lastName: session?.user?.lastName || null,
        emailVerified: session?.user?.emailVerified || null,
        phone: session?.user?.phone || null,
        image: session?.user?.image || null,
        companyId: session?.user?.companyId || null,
        companyName: session?.user?.companyName || null,
        companyImage: session?.user?.companyImage || null,
        paymentTerms: session?.user?.paymentTerms || "cash_on_delivery",
        stripeCustomerId: session?.user?.stripeCustomerId || null,
        tierId: session?.user?.tierId || null,
        addressId: session?.user?.addressId || null,
        permissions: session?.user?.permissions || {},
      };

      // Immediately create a local cart representation for faster UI
      const newLocalCart: Cart = {
        id: `temp-${Date.now()}`,
        quote_number: newQuoteNumber,
        name: newName,
        user_data: defaultUserData,
        cart_items: [],
        printed: false,
        discarded: false,
        purchased: false,
        notes: "",
        created: new Date(),
        updated: new Date(),
        isServerCart: !!session?.user,
      };

      // Make sure to clean the cart before storing to avoid circular references
      const cleanLocalCart = prepareCartForStorage(newLocalCart);

      // Update local storage with this new cart
      try {
        localStorage.setItem(
          "active-cart-data",
          JSON.stringify(cleanLocalCart)
        );
      } catch (storageError) {
        console.error(
          "[CartButton] Failed to update localStorage:",
          storageError
        );
      }

      // For non-authenticated users, make sure the cart is also in the zustand store
      // Get direct access to the store and make sure it's properly added
      const store = useCartStore.getState();

      // Check if cart already exists in store
      const existingCart = store.carts.find(
        (c) => c.quote_number === newQuoteNumber
      );
      if (!existingCart) {
        store.importCart(cleanLocalCart);

        // Set it as active in the store
        store.setActiveCart(newQuoteNumber);
      }

      // Always update the open carts in the zustand store
      // Get the current list of open carts and add the new cart to the end
      const allCarts = store.carts.map((cart) => cart.quote_number);

      // Remove the new cart if it exists anywhere in the array (to avoid duplicates)
      const filteredCarts = allCarts.filter((qn) => qn !== newQuoteNumber);

      // Add the new cart explicitly to the end to ensure it appears on the right
      const updatedOpenCarts = [...filteredCarts, newQuoteNumber];

      // Update the order in both places to ensure consistency
      store.updateCartsOrder(updatedOpenCarts);
      saveOpenServerCarts(updatedOpenCarts);

      // Set the new cart as active and update persistent tab ID
      setActiveCart(newQuoteNumber);
      setPersistentTabId(newQuoteNumber);

      // Dispatch a custom event to notify carts-client about the new cart
      window.dispatchEvent(
        new CustomEvent("cart-created", {
          detail: { quoteNumber: newQuoteNumber },
        })
      );

      // Refresh tabs with a slight delay to allow state updates to settle
      setTimeout(() => {
        refreshTabs();
      }, 100);
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to create new cart: " + err,
        variant: "destructive",
      });
    } finally {
      setIsAddingNewCart(false);
    }
  };

  const handleDownloadQuote = async () => {
    if (!activeQuoteNumber) return;

    try {
      setIsLoadingQuote(true);

      // Store the final quote number we'll use for the PDF
      let finalQuoteNumber = activeQuoteNumber;
      let cartItemsForPdf: CartItem[] | null = null;

      // If user is not authenticated and this is a local cart, convert it to server cart
      if (!session && activeCart && !activeCart.isServerCart) {
        // Ensure all cart items have tier_ids before conversion
        if (activeCart.cart_items && activeCart.cart_items.length > 0) {
          const defaultTierId = "460a3d62-c14a-4b55-8045-7ec079527d47"; // Default tier ID
          const itemsNeedingTierIds = activeCart.cart_items.filter(
            (item) => !item.tier_id || item.tier_id === ""
          );

          if (itemsNeedingTierIds.length > 0) {
            // Update the cart store with fixed tier_ids
            const store = useCartStore.getState();
            const fixedCartItems = activeCart.cart_items.map((item) => ({
              ...item,
              tier_id: item.tier_id || defaultTierId,
            }));

            store.setItems(fixedCartItems);
          }
        }

        try {
          const serverCart = await convertGuestCartToServer(activeQuoteNumber);

          if (!serverCart) {
            throw new Error("Failed to convert cart to server");
          }

          // Use the cart items from the server response
          if (serverCart.cart_items && serverCart.cart_items.length > 0) {
            cartItemsForPdf = serverCart.cart_items;
          } else {
            cartItemsForPdf = activeCart.cart_items;
          }

          finalQuoteNumber = serverCart.quote_number;

          // Update the local cart store to reflect the server cart state
          const store = useCartStore.getState();
          store.updateCart(serverCart.quote_number, {
            isServerCart: true,
            printed: true,
          });

          // Update localStorage to reflect the printed flag
          try {
            const updatedCartForStorage = {
              ...serverCart,
              printed: true,
            };
            localStorage.setItem(
              "active-cart-data",
              JSON.stringify(prepareCartForStorage(updatedCartForStorage))
            );
          } catch (storageError) {
            console.error(
              "[CartButton] Failed to update localStorage:",
              storageError
            );
          }

          // Update the URL with the new quote number if it changed
          if (activeQuoteNumber !== serverCart.quote_number) {
            // Update the active quote number to the new server-assigned one
            setActiveCart(serverCart.quote_number);

            // If we're on the checkout page, update the URL
            if (window.location.pathname.includes("/checkout")) {
              const newUrl = `/checkout?quote=${serverCart.quote_number}`;
              window.history.replaceState(null, "", newUrl);
            }
          }

          // Force tab refresh to show the server badge
          setTabsKey((prev) => prev + 1);

          toast({
            title: "Success",
            description:
              "Quote saved to server and ready for download. Create an account to access this quote later!",
          });
        } catch (conversionError) {
          console.error(
            "[CartButton] Error converting cart to server:",
            conversionError
          );
          toast({
            title: "Warning",
            description:
              "Unable to save quote to server. Download will proceed but quote may not be retrievable later.",
            variant: "destructive",
          });
          // Continue with the original cart items
          cartItemsForPdf = activeCart.cart_items;

          // For local carts that remain local, only set printed flag
          if (!activeCart.isServerCart) {
            const store = useCartStore.getState();
            store.updateCart(activeQuoteNumber, {
              printed: true,
            });

            // Update localStorage
            try {
              const updatedCartForStorage = {
                ...activeCart,
                printed: true,
              };
              localStorage.setItem(
                "active-cart-data",
                JSON.stringify(prepareCartForStorage(updatedCartForStorage))
              );
            } catch (storageError) {
              console.error(
                "[CartButton] Failed to update localStorage:",
                storageError
              );
            }
          }
        }
      } else {
        // Cart is already a server cart, use its items
        if (activeCart && activeCart.cart_items) {
          cartItemsForPdf = activeCart.cart_items;
        }
      }

      // Check if we have cart items to generate the PDF
      if (!cartItemsForPdf || cartItemsForPdf.length === 0) {
        console.error(
          "[CartButton] No cart items available for PDF generation"
        );
        // Try one more time to get cart items from the current state
        const currentCart = availableCarts.find(
          (c) => c.quote_number === finalQuoteNumber
        );
        if (currentCart?.cart_items && currentCart.cart_items.length > 0) {
          cartItemsForPdf = currentCart.cart_items;
        } else {
          toast({
            title: "Error",
            description:
              "Cannot generate a quote with no items. Please try again in a moment.",
            variant: "destructive",
          });
          return;
        }
      }

      // Format current date for PDF
      const today = new Date();
      const formattedDate = today.toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });

      // Generate the PDF
      if (!generateQuotePDF) {
        throw new Error("Quote PDF generation not available");
      }

      const pdfBase64 = await generateQuotePDF(
        cleanCartItemsForServerAction(cartItemsForPdf),
        finalQuoteNumber,
        formattedDate,
        session?.user as UserSession
      );

      // Create a download link for the PDF
      const link = document.createElement("a");
      link.href = `data:application/pdf;base64,${pdfBase64}`;
      link.download = `Quote-${finalQuoteNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast({
        title: "Success",
        description: "Quote downloaded successfully",
      });
    } catch (err) {
      console.error("[CartButton] Error generating quote PDF:", err);
      toast({
        title: "Error",
        description: "Failed to download quote: " + err,
        variant: "destructive",
      });
    } finally {
      setIsLoadingQuote(false);
    }
  };

  const handleFindQuote = async () => {
    if (!searchQuoteNumber.trim()) return;

    try {
      setIsSearching(true);
      const result = await getCart(searchQuoteNumber.trim());

      if (result.success) {
        // For server carts, inform that it's been retrieved from server
        if (result.cart?.isServerCart) {
          toast({
            title: "Cart Retrieved",
            description: `Quote ${searchQuoteNumber} has been loaded from the server`,
          });
        } else {
          toast({
            title: "Cart Found",
            description: `Quote ${searchQuoteNumber} has been loaded`,
          });
        }
        setQuoteSearchOpen(false);
        setSearchQuoteNumber("");
        refreshTabs();
      } else {
        toast({
          title: "Quote Not Found",
          description: result.error || "Unable to find quote with that number",
          variant: "destructive",
        });
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to search for quote: " + err,
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleRenameCart = async (quoteNumber: string, newName: string) => {
    try {
      if (!newName.trim()) {
        // Don't update if new name is empty
        return;
      }

      // Find the cart to check if it's a server cart
      const cart = availableCarts.find((c) => c.quote_number === quoteNumber);
      if (!cart) {
        console.error(
          `[CartButton] Cart not found for renaming: ${quoteNumber}`
        );
        return;
      }

      // Only show loading state for server carts
      if (cart.isServerCart) {
        setRenamingCartId(quoteNumber);
      }

      // Try to rename the cart
      await renameCart(quoteNumber, newName);

      // Force refresh of tabs
      refreshTabs();
    } catch (err) {
      console.error("[CartButton] Rename cart error:", err);
      toast({
        title: "Error",
        description: "Failed to rename cart: " + err,
        variant: "destructive",
      });

      // Force refresh to revert to original name
      refreshTabs();
    } finally {
      // Clear renaming state
      setRenamingCartId(null);
    }
  };

  const handleTabChange = (tabId: string) => {
    setActiveCart(tabId);
    setPersistentTabId(tabId);

    // If we're on the checkout page, update the URL
    if (window.location.pathname.includes("/checkout")) {
      const newUrl = `/checkout?quote=${tabId}`;
      window.history.replaceState(null, "", newUrl);
    }
  };

  // Add new function to handle tab reordering
  const handleTabsReorder = useCallback(
    (
      tabs: {
        id: string;
        title: string | JSX.Element;
        content: React.ReactNode;
      }[]
    ) => {
      // Skip if no tabs to reorder
      if (!tabs || tabs.length === 0 || !tabs[0]) {
        return;
      }

      // Extract quote numbers in the new order
      const quoteNumbers = tabs.map((tab) => tab.id);

      // Skip if no quote numbers to reorder
      if (!quoteNumbers.length) {
        return;
      }

      // Update the cart order through the provider
      updateCartsOrder(quoteNumbers);

      saveOpenServerCarts(quoteNumbers);

      // Force tabs refresh
      refreshTabs();
    },
    [refreshTabs, saveOpenServerCarts, updateCartsOrder]
  );

  const cartTabs = useMemo(() => {
    if (!Array.isArray(availableCarts) || availableCarts.length === 0) {
      return [];
    }

    // Filter out any potential undefined or null carts
    // This ensures we don't create tabs for carts that have been removed
    const validCarts = availableCarts.filter(
      (cart: Cart) => cart && cart.quote_number && !cart.purchased
    );

    // Create a Set to track used IDs and prevent duplicates
    const usedIds = new Set<string>();

    return validCarts.map((cart) => {
      // Ensure cart_items is always an array
      const cartItems = Array.isArray(cart.cart_items) ? cart.cart_items : [];

      // Check if THIS SPECIFIC cart is empty - use current items from context if this is the active cart
      let isCartEmpty;
      if (cart.quote_number === activeQuoteNumber) {
        // For the active cart, use the current items from context which reflects real-time changes
        isCartEmpty = items.length === 0;
      } else {
        // For other carts, use their stored cart_items
        isCartEmpty = cartItems.length === 0;
      }

      const isRenaming = renamingCartId === cart.quote_number;

      // Ensure we have a unique ID for each tab
      let tabId = cart.quote_number;

      // If this ID is already used (shouldn't happen, but as a safeguard)
      if (usedIds.has(tabId)) {
        tabId = `${tabId}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      }

      // Track this ID
      usedIds.add(tabId);

      // Create a clean version of the cart (without the _group reference) for tab content
      // We don't need to use the cleaned cart in the current context, so we can omit it
      prepareCartForStorage(cart);

      return {
        id: tabId,
        title: (
          <div className='flex items-center gap-2'>
            {cart.isServerCart ? (
              isRenaming ? (
                <span className='animate-spin h-3 w-3 border-2 border-foreground rounded-md border-t-transparent' />
              ) : (
                <CloudIcon className='h-3 w-3' />
              )
            ) : (
              <CloudOffIcon className='h-3 w-3' />
            )}
            <span>{cart.name || cart.quote_number || "New"}</span>
          </div>
        ),
        content: (
          <div>
            {isCartEmpty ? (
              <div className='h-[calc(100vh-260px-180px)] md:h-[calc(100vh-65px-300px)] px-4 w-full flex items-center justify-center'>
                <EmptyCart isInSheet={true} />
              </div>
            ) : (
              <div className='space-y-2'>
                <div className='p-4'>
                  <Button
                    variant='outline'
                    size='sm'
                    className='flex items-center gap-1 border w-full'
                    onClick={handleClearCartClick}
                    disabled={isClearingCart}
                  >
                    <Trash2 className='h-4 w-4' />
                    <span>{isClearingCart ? "Emptying..." : "Empty Cart"}</span>
                  </Button>
                </div>
                {cart.isServerCart && (
                  <div className='text-sm text-muted-foreground px-4'>
                    Quote: {cart.quote_number}
                  </div>
                )}
                <ScrollArea className='h-[calc(100vh-260px-180px)] md:h-[calc(100vh-65px-230px)] px-4 w-full'>
                  <CartItems isSheet={true} isLoading={isLoading} />
                </ScrollArea>
              </div>
            )}
          </div>
        ),
      };
    });
  }, [
    availableCarts,
    handleClearCartClick,
    isLoading,
    renamingCartId,
    isClearingCart,
    items,
    activeQuoteNumber,
  ]);

  // Add an effect to listen for sign-out events to clear cart state
  useEffect(() => {
    if (!isMounted) return;

    // Create a function to handle sign-out events from localStorage
    const handleStorageChange = (e: StorageEvent) => {
      // Check if the user is signing out by monitoring if user-carts is removed
      if (e.key === "user-carts" && e.newValue === null) {
        // Clear local cart data state
        setLocalCartData(null);
        setLocalCartItemCount(0);
      }
    };

    // Function to handle direct signout event
    const handleSignOut = () => {
      // Clear component state
      setLocalCartData(null);
      setLocalCartItemCount(0);

      // Clear Zustand store for extra safety
      const store = useCartStore.getState();
      store.clearAllCarts();

      // Force removal from localStorage as well (in case other methods failed)
      try {
        localStorage.removeItem("user-carts");
        localStorage.removeItem("active-cart-data");
      } catch (e) {
        console.error("[CartButton] Error clearing localStorage:", e);
      }
    };

    // Listen for storage events to detect sign-out across tabs
    window.addEventListener("storage", handleStorageChange);

    // Listen for our custom signout event
    window.addEventListener("user-signout", handleSignOut);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("user-signout", handleSignOut);
    };
  }, [isMounted]);

  // Listen for 'open-cart' event to open the cart Sheet, only if this CartButton matches the current viewport
  useEffect(() => {
    if (typeof isMobile === "undefined") return;
    if ((isMobile && isMobileDevice) || (!isMobile && !isMobileDevice)) {
      const openCart = () => setIsCartOpen(true);
      window.addEventListener("open-cart", openCart);
      return () => window.removeEventListener("open-cart", openCart);
    }
  }, [isMobile, isMobileDevice]);

  // Swipe handlers for cart sheet
  const swipeHandlers = useSwipeable({
    onSwipedRight: () => {
      setIsCartOpen(false);
    },
    trackMouse: false,
    trackTouch: true,
    preventScrollOnSwipe: false,
    delta: 50, // Minimum swipe distance
  });

  return (
    <div className={cn("flex items-center gap-4", className)}>
      <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
        <SheetTrigger asChild disabled={disabled}>
          <Button
            variant='ghost'
            size='icon'
            className={cn(
              "relative flex items-center justify-center h-10 w-10 rounded-md",
              className
            )}
            aria-label='Open cart'
            onClick={disabled ? undefined : () => setIsCartOpen(true)}
            disabled={disabled}
          >
            {shouldShowLoading ? (
              <div className='animate-spin h-5 w-5 border-2 border-foreground rounded-full border-t-transparent' />
            ) : (
              <ShoppingCart
                className={cn("h-5 w-5", disabled && "text-muted-foreground")}
              />
            )}
            {!shouldShowLoading &&
              (localCartItemCount > 0 || getTotalItemCount(items) > 0) && (
                <span className='absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center'>
                  {isMounted ? getTotalItemCount(items) : localCartItemCount}
                </span>
              )}
          </Button>
        </SheetTrigger>

        <SheetContent
          side='right'
          className='flex flex-col w-full md:max-w-[400px] md:w-[400px] z-50 bg-sidebar p-0 mt-16 h-[calc(100dvh-4rem-4rem)] lg:h-[calc(100dvh-4rem)]'
          {...swipeHandlers}
        >
          <div className='flex flex-col h-full'>
            <SheetHeader className='px-4 py-3 border-b border-muted shrink-0'>
              <div className='flex justify-between items-center'>
                <SheetTitle>
                  Cart (
                  {isMounted ? getTotalItemCount(items) : localCartItemCount})
                </SheetTitle>
                <div className='flex items-center gap-2'>
                  {session && (
                    <Button variant='ghost' size='icon' asChild>
                      <Link href='/quotes'>
                        <ShoppingCart className='h-4 w-4' />
                      </Link>
                    </Button>
                  )}
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => setQuoteSearchOpen(true)}
                    title='Find a Quote'
                  >
                    <Search className='h-4 w-4' />
                  </Button>
                </div>
                <SheetDescription className='sr-only'>
                  Shopping cart containing your selected items
                </SheetDescription>
              </div>
            </SheetHeader>

            <div className='flex-1 overflow-hidden'>
              <div className='flex flex-col h-full'>
                <BrowserTabs
                  key={`browser-tabs-${tabsKey}`}
                  initialTabs={cartTabs}
                  defaultTabId={
                    persistentTabId || activeQuoteNumber || undefined
                  }
                  onTabChange={handleTabChange}
                  onTabClose={(tabId) => handleCloseCartTabCallback(tabId)}
                  onTabAdd={handleAddNewCart}
                  onTabRename={(tabId, newName) =>
                    handleRenameCart(tabId, newName)
                  }
                  onTabsReorder={handleTabsReorder}
                  isAddingTab={isAddingNewCart}
                />
              </div>
            </div>

            {/* Cart Summary */}
            <div className='space-y-4 border-t border-muted shrink-0 p-4'>
              <div className='space-y-1.5'>
                <div className='flex justify-between text-sm'>
                  <span>Shipping</span>
                  <span className='text-muted-foreground'>
                    Calculated at checkout
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span>Total</span>
                  <span>
                    AUD
                    {formatPrice(getTotalPrice())}{" "}
                    <span className='text-xs text-muted-foreground'>
                      excl. GST
                    </span>
                  </span>
                </div>
              </div>
              <div className='flex flex-col gap-2'>
                <SheetClose asChild>
                  <Button
                    className='w-full'
                    size='lg'
                    asChild
                    disabled={!items.length}
                    onClick={async (e) => {
                      if (activeCart) {
                        if (
                          !session &&
                          activeCart &&
                          !activeCart.isServerCart
                        ) {
                          // Prevent default navigation
                          e.preventDefault();

                          try {
                            // Use the cart provider function to convert the cart
                            const serverCart = await convertGuestCartToServer(
                              activeCart.quote_number
                            );

                            if (serverCart && serverCart.quote_number) {
                              // Navigate to checkout with the server cart quote number
                              window.location.href = `/checkout?quote=${serverCart.quote_number}`;
                            } else {
                              toast({
                                title: "Error",
                                description:
                                  "Unable to save cart to server. Please try again.",
                                variant: "destructive",
                              });

                              // Default fallback - navigate with the original quote number
                              window.location.href = `/checkout?quote=${activeCart.quote_number}`;
                            }
                          } catch (err) {
                            console.error(
                              "Error converting cart for checkout:",
                              err
                            );
                            toast({
                              title: "Error",
                              description:
                                "Failed to prepare cart for checkout. Please try again.",
                              variant: "destructive",
                            });

                            // Navigate anyway as a fallback
                            window.location.href = `/checkout?quote=${activeCart.quote_number}`;
                          }
                        } else {
                          // For server carts or logged-in users, just set the active cart and proceed
                          setActiveCart(activeCart.quote_number);
                        }
                      }
                    }}
                  >
                    <Link
                      href={
                        activeCart
                          ? `/checkout?quote=${activeCart.quote_number}`
                          : "/checkout"
                      }
                    >
                      Proceed to Checkout
                    </Link>
                  </Button>
                </SheetClose>

                <Button
                  variant='outline'
                  className='w-full flex items-center gap-2'
                  onClick={handleDownloadQuote}
                  disabled={!items.length || isLoadingQuote}
                >
                  <Download className='h-4 w-4' />
                  {isLoadingQuote ? "Downloading..." : "Download Quote"}
                </Button>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={quoteSearchOpen} onOpenChange={setQuoteSearchOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Find a Quote</DialogTitle>
            <DialogDescription>
              Enter a quote number to load its items into your cart.
              {session && (
                <span className='block mt-2 text-primary'>
                  This will also retrieve any carts you have previously hidden.
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4 py-4'>
            <div className='space-y-2'>
              <Label htmlFor='quoteNumber'>Quote Number</Label>
              <Input
                id='quoteNumber'
                placeholder='e.g. QT-12345'
                value={searchQuoteNumber}
                onChange={(e) => setSearchQuoteNumber(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setQuoteSearchOpen(false)}
              disabled={isSearching}
            >
              Cancel
            </Button>
            <Button
              onClick={handleFindQuote}
              disabled={!searchQuoteNumber.trim() || isSearching}
            >
              {isSearching ? "Searching..." : "Load Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={clearCartAlertOpen}
        onOpenChange={setClearCartAlertOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Empty Cart</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove all items from your cart?
              {activeCart?.isServerCart && (
                <span className='block mt-2 font-medium text-primary'>
                  This will permanently remove all items from your saved quote.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isClearingCart}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleClearCart}
              disabled={isClearingCart}
              className='bg-destructive hover:bg-destructive/90'
            >
              {isClearingCart ? "Emptying..." : "Empty Cart"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export { CartButton };

// For loading state
export function CartButtonSkeleton() {
  return (
    <div className='flex items-center gap-4'>
      <div className='hidden md:block'>
        <div className='h-4 w-16 bg-muted animate-pulse rounded' />
      </div>
      <div className='h-10 w-10 bg-muted animate-pulse rounded-md' />
    </div>
  );
}
